import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { noop } from 'lodash';
import { InView } from 'react-intersection-observer';
import VerifiedIcon from 'images/verified.svg';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { emitTrackInlineContactFormViewEvent } from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import { Button } from '../../../ui/Button';
import { ContactForm } from '../ContactForm/ContactForm';
import { ContactFormQualifier } from '../ContactForm/ContactFormQualifier';
import { useQualifierGate } from '../ContactForm/useQualifierGate';
import { useHaloContactSubmission } from '../ContactForm/useHaloContactSubmission';
import * as formStyles from '../ContactForm/contactForm.styles';
import * as styles from './inlineContactModule.styles';

export const InlineContactModule = () => {
  const isMobile = useSelector((state: RootReduxState) => state.app.device.screenWidth === 'sm');
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const user = useSelector((state: RootReduxState) => state.user);

  if (isMobile || !listing || listing.active === false) {
    return null;
  }

  return <InlineContactModuleInner listing={listing} user={user} />;
};

type InlineContactModuleInnerProps = {
  listing: ListingDetails;
  user: RootReduxState['user'];
};

const INLINE_FORM_ID_PREFIX = 'halo-inline-contact';

const InlineContactModuleInner = ({ listing, user }: InlineContactModuleInnerProps) => {
  const { handleSubmitForm, isLoading, serverErrors } = useHaloContactSubmission({
    listing,
    onClose: noop,
    formMode: 'inline',
  });
  const gate = useQualifierGate(listing);
  const handleInlineContactInView = useCallback(
    (inView: boolean) => {
      if (inView) {
        emitTrackInlineContactFormViewEvent({ listing });
      }
    },
    [listing],
  );

  const sendButtonLabel = listing.waitlisted ? 'Add me to the waitlist' : 'Send message';

  // While the qualifier is visible, the form + submit + disclaimer are not
  // interactive. `inert` is valid HTML but not yet in React's DOM types.
  const inertProps = gate.showQualifier
    ? ({ inert: '', 'aria-hidden': true } as unknown as Record<string, unknown>)
    : {};

  const contactCard = (
    <>
      <div className={styles.innerCard}>
        {(listing?.displayName || listing.trusted) && (
          <div className={styles.heading}>
            {listing?.displayName && <h2 className={styles.subheading}>{listing.displayName}</h2>}
            {listing.trusted && (
              <div className={styles.verifiedBadge}>
                <img src={VerifiedIcon} alt="" aria-hidden="true" className={styles.verifiedIcon} />
                <span>Verified listing</span>
              </div>
            )}
          </div>
        )}
        <ContactForm
          listing={listing}
          onSubmit={handleSubmitForm}
          isLoading={isLoading}
          serverErrors={serverErrors}
          userInquiryDataCache={user?.inquiryDataCache}
          user={user}
          idPrefix={INLINE_FORM_ID_PREFIX}
        />
        <div className={styles.submitButtonWrapper}>
          <Button
            type="submit"
            form={`${INLINE_FORM_ID_PREFIX}-form`}
            shape="rectangle"
            tone="brand"
            emphasis="filled"
            fluid={true}
            disabled={isLoading}
            aria-disabled={isLoading}
          >
            {isLoading ? 'Sending...' : sendButtonLabel}
          </Button>
        </div>
      </div>
      <p className={styles.disclaimerOnTeal}>
        By contacting this property, you agree to our{' '}
        <a
          href="https://www.zillow.com/renter-hub/terms/Rental-User-Terms"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.disclaimerOnTealLink}
        >
          Terms of Use
        </a>
        . Visit our{' '}
        <a
          href="https://www.zillowgroup.com/zg-privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.disclaimerOnTealLink}
        >
          Privacy Portal
        </a>{' '}
        for more information. When you click &ldquo;{sendButtonLabel}&rdquo;, we&rsquo;ll send your inquiry to the
        property manager so they can reach out and answer your questions.
      </p>
    </>
  );

  return (
    <div className={styles.wrapperRelative}>
      {gate.showQualifier ? (
        <div className={styles.tealFrame} {...inertProps}>
          {contactCard}
        </div>
      ) : (
        <InView as="div" className={styles.tealFrame} triggerOnce onChange={handleInlineContactInView}>
          {contactCard}
        </InView>
      )}
      {isLoading && (
        <>
          <div className={styles.loadingOverlay} aria-hidden="true">
            <div className={styles.loadingSpinner} />
          </div>
          <div className={formStyles.srOnly} role="status" aria-live="polite">
            Sending your message...
          </div>
        </>
      )}
      {gate.showQualifier && gate.qualifierVariant && (
        <ContactFormQualifier variant={gate.qualifierVariant} listing={listing} onContinue={gate.acknowledge} />
      )}
    </div>
  );
};
